import type { RefObject } from "react";

import {
  Box,
  Card,
  Flex,
  SegmentedControl,
  Table,
  Text,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import { directionColor, directionOf } from "../overview.format";
import type {
  OverviewActivityFilter,
  RecentActivityCardPresentationProps as SharedRecentActivityCardPresentationProps,
} from "../types";
import { OVERVIEW_ACTIVITY_FILTER_OPTIONS } from "./overviewActivities.utils";

export interface RecentActivityCardMeasurementProps {
  headerRowRef: RefObject<HTMLTableRowElement>;
  probeRowRef: RefObject<HTMLTableRowElement>;
  headerHeight: number;
  reservedHeight: number;
}

export type RecentActivityCardPresentationProps = Omit<
  SharedRecentActivityCardPresentationProps,
  "isLoading" | "isAccountReady"
> &
  RecentActivityCardMeasurementProps & {
    emptyMessage: string;
  };

/** Non-breaking space: gives the probe row or the footer a line box to fill. */
const RESERVED_LINE = "\u00A0";

export const RecentActivityCardPresentation = ({
  rows,
  totalCount,
  filter,
  onFilterChange,
  rowLimit,
  emptyMessage,
  headerRowRef,
  probeRowRef,
  headerHeight,
  reservedHeight,
  hasError,
}: RecentActivityCardPresentationProps) => {
  const truncated = totalCount > rowLimit;

  return (
    <Flex
      width="100%"
      minWidth="0"
      maxWidth={{ initial: "100%", sm: "640px" }}
      data-testid="overview-activity-card"
    >
      <Card size="2" className="flex-1">
        <Flex direction="column" gap="3" height="100%">
          <Flex align="start" justify="between" gap="3" wrap="wrap">
            <Flex direction="column" gap="1">
              <Text size="4" weight="bold">
                Recent activity
              </Text>
              <Text size="1" color="gray">
                Last 30 days · {totalCount}{" "}
                {totalCount === 1 ? "entry" : "entries"}
              </Text>
            </Flex>
            <SegmentedControl.Root
              size="1"
              value={filter}
              onValueChange={(value) =>
                value && onFilterChange(value as OverviewActivityFilter)
              }
              aria-label="Filter activities by category"
            >
              {OVERVIEW_ACTIVITY_FILTER_OPTIONS.map((option) => (
                <SegmentedControl.Item key={option.value} value={option.value}>
                  {option.label}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl.Root>
          </Flex>

          {/* Measured reserve holds the height of `rowLimit` rows, so a short
            result keeps the card's size without placeholder rows. No `min-h-*`
            class may sit here: the UI preset builds Tailwind with
            `important: true`, so one would out-rank this inline reserve. */}
          <Box
            flexGrow="1"
            className="relative overflow-auto"
            data-testid="overview-activity-table-region"
            style={
              reservedHeight > 0 ? { minHeight: reservedHeight } : undefined
            }
          >
            <Table.Root variant="ghost" size="1">
              <Table.Header>
                <Table.Row
                  ref={headerRowRef}
                  data-testid="overview-activity-header-row"
                >
                  <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell align="right">
                    Date
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell align="right">
                    Amount
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body data-testid="overview-activity-rows">
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell>
                      <Text size="1" weight="medium">
                        {row.description}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="1" color="gray">
                        {row.typeLabel}
                      </Text>
                    </Table.Cell>
                    <Table.Cell align="right">
                      <Text
                        size="1"
                        color="gray"
                        className="atd-numeric whitespace-nowrap"
                      >
                        {row.dateLabel}
                      </Text>
                    </Table.Cell>
                    <Table.Cell align="right">
                      <Text
                        size="1"
                        color={
                          row.amount === undefined
                            ? "gray"
                            : directionColor(directionOf(row.amount))
                        }
                        className="atd-numeric whitespace-nowrap"
                      >
                        {row.amountLabel}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>

            {/* Out of flow and never read out: it exists only to be measured. */}
            <Box
              aria-hidden
              className="pointer-events-none invisible absolute inset-x-0 top-0"
            >
              <Table.Root variant="ghost" size="1">
                <Table.Body>
                  <Table.Row
                    ref={probeRowRef}
                    data-testid="overview-activity-row-probe"
                  >
                    <Table.Cell>
                      <Text size="1">{RESERVED_LINE}</Text>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </Box>

            {rows.length === 0 ? (
              // Starts below the header row so the message never crowds it.
              <Flex
                align="center"
                justify="center"
                data-testid="overview-activity-empty"
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{ top: headerHeight }}
              >
                <Text size="1" color={hasError ? "red" : "gray"}>
                  {emptyMessage}
                </Text>
              </Flex>
            ) : null}
          </Box>

          <Text
            size="1"
            color="gray"
            data-testid="overview-activity-footer"
            aria-hidden={!truncated}
            className={cn(!truncated && "invisible")}
          >
            {truncated
              ? `Showing ${rows.length} of ${totalCount}. Open the Activities widget for the full log.`
              : RESERVED_LINE}
          </Text>
        </Flex>
      </Card>
    </Flex>
  );
};
